import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Building2, Globe, Phone, Mail,
  ChevronRight, Shield, Wrench, Package, MoreVertical,
  ExternalLink, Edit, Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const DEPARTMENTS = [
  { value: 'sav', label: 'SAV' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'production', label: 'Production' },
  { value: 'logistics', label: 'Logistique' },
  { value: 'administration', label: 'Administration' },
  { value: 'direction', label: 'Direction' },
  { value: 'technique', label: 'Technique' },
];

const ContactsManager = ({ contacts, onChange }) => {
  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ department: 'sav', first_name: '', last_name: '', position: '', email: '', phone: '' });

  const resetForm = () => {
    setForm({ department: 'sav', first_name: '', last_name: '', position: '', email: '', phone: '' });
    setEditIdx(null);
    setShowForm(false);
  };

  const handleSave = () => {
    if (!form.last_name && !form.email) { toast.error("Nom ou email requis"); return; }
    const updated = [...(contacts || [])];
    if (editIdx !== null) {
      updated[editIdx] = form;
    } else {
      updated.push(form);
    }
    onChange(updated);
    resetForm();
  };

  const handleEdit = (idx) => {
    setForm({ ...contacts[idx] });
    setEditIdx(idx);
    setShowForm(true);
  };

  const handleDelete = (idx) => {
    const updated = contacts.filter((_, i) => i !== idx);
    onChange(updated);
  };

  const deptColors = {
    sav: '#EF4444', commercial: '#3B82F6', marketing: '#EC4899',
    production: '#F59E0B', logistics: '#14B8A6', administration: '#8B5CF6',
    direction: '#00E01A', technique: '#F97316',
  };

  return (
    <div className="space-y-3" data-testid="contacts-manager">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Contacts ({(contacts || []).length})</Label>
        {!showForm && (
          <button
            type="button"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-1 text-xs text-[#00E01A] hover:text-[#00B815] transition-colors"
            data-testid="add-contact-btn"
          >
            <Plus size={14} /> Ajouter
          </button>
        )}
      </div>

      {/* Existing contacts */}
      {(contacts || []).map((c, idx) => (
        <div key={idx} className="bg-[#141617] rounded-lg p-3 flex items-start gap-3" data-testid={`contact-${idx}`}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
            style={{ backgroundColor: deptColors[c.department] || '#8C8F94' }}>
            {(c.first_name?.[0] || '') + (c.last_name?.[0] || '?')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-medium">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase"
                style={{ backgroundColor: (deptColors[c.department] || '#8C8F94') + '20', color: deptColors[c.department] || '#8C8F94' }}>
                {DEPARTMENTS.find(d => d.value === c.department)?.label || c.department}
              </span>
            </div>
            {c.position && <p className="text-[#8C8F94] text-xs">{c.position}</p>}
            <div className="flex gap-3 mt-1 text-xs text-[#8C8F94]">
              {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
            </div>
          </div>
          <div className="flex gap-1">
            <button type="button" onClick={() => handleEdit(idx)} className="text-[#8C8F94] hover:text-white p-1" data-testid={`edit-contact-${idx}`}><Edit size={12} /></button>
            <button type="button" onClick={() => handleDelete(idx)} className="text-[#8C8F94] hover:text-[#EF4444] p-1" data-testid={`delete-contact-${idx}`}><Trash2 size={12} /></button>
          </div>
        </div>
      ))}

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-[#141617] rounded-lg p-3 space-y-2 border border-[#2C3338]" data-testid="contact-form">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px]">Service *</Label>
              <Select value={form.department} onValueChange={v => setForm(p => ({...p, department: v}))}>
                <SelectTrigger className="h-8 text-xs" data-testid="contact-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Prénom</Label>
              <Input className="h-8 text-xs" value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} data-testid="contact-firstname" />
            </div>
            <div>
              <Label className="text-[10px]">Nom</Label>
              <Input className="h-8 text-xs" value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} data-testid="contact-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px]">Poste</Label>
              <Input className="h-8 text-xs" value={form.position} onChange={e => setForm(p => ({...p, position: e.target.value}))} placeholder="Resp. SAV" data-testid="contact-position" />
            </div>
            <div>
              <Label className="text-[10px]">Email</Label>
              <Input className="h-8 text-xs" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} data-testid="contact-email" />
            </div>
            <div>
              <Label className="text-[10px]">Téléphone</Label>
              <Input className="h-8 text-xs" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} data-testid="contact-phone" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" className="bg-[#00E01A] hover:bg-[#00B815] text-black text-xs h-7 px-3" onClick={handleSave} data-testid="save-contact-btn">
              {editIdx !== null ? 'Modifier' : 'Ajouter'}
            </Button>
            <Button type="button" variant="outline" size="sm" className="text-xs h-7 px-3" onClick={resetForm}>Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SupplierForm = ({ supplier, onSave, onClose }) => {
  const [formData, setFormData] = useState(supplier || {
    name: '',
    country: 'France',
    website: '',
    relation_type: 'distributor',
    default_warranty_months: 24,
    sav_portal_url: '',
    status: 'active',
    labor_rate_per_hour: '',
    discount_percent: '',
    payment_terms: '',
    avg_delivery_days: '',
    contacts: []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nom du fournisseur *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Awake"
            required
            data-testid="supplier-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">Pays</Label>
          <Input
            id="country"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            placeholder="France"
            data-testid="supplier-country-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="website">Site web</Label>
          <Input
            id="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://..."
            data-testid="supplier-website-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="relation_type">Type de relation</Label>
          <Select
            value={formData.relation_type}
            onValueChange={(value) => setFormData({ ...formData, relation_type: value })}
          >
            <SelectTrigger data-testid="supplier-relation-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exclusive_importer">Importateur exclusif</SelectItem>
              <SelectItem value="authorized_reseller">Revendeur agréé</SelectItem>
              <SelectItem value="distributor">Distributeur</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="warranty">Garantie par défaut (mois)</Label>
          <Input
            id="warranty"
            type="number"
            value={formData.default_warranty_months}
            onChange={(e) => setFormData({ ...formData, default_warranty_months: parseInt(e.target.value) || 24 })}
            data-testid="supplier-warranty-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="labor_rate">Tarif MO (€/h)</Label>
          <Input
            id="labor_rate"
            type="number"
            value={formData.labor_rate_per_hour}
            onChange={(e) => setFormData({ ...formData, labor_rate_per_hour: parseFloat(e.target.value) || '' })}
            placeholder="85"
            data-testid="supplier-labor-rate-input"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="logo_url">URL du logo</Label>
          <Input
            id="logo_url"
            value={formData.logo_url || ''}
            onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
            placeholder="https://... (URL de l'image du logo)"
            data-testid="supplier-logo-input"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="sav_portal">URL Portail SAV</Label>
          <Input
            id="sav_portal"
            value={formData.sav_portal_url}
            onChange={(e) => setFormData({ ...formData, sav_portal_url: e.target.value })}
            placeholder="https://..."
            data-testid="supplier-sav-portal-input"
          />
        </div>
      </div>

      {/* Contacts Manager */}
      <div className="pt-4 border-t border-[#2C3338]">
        <ContactsManager
          contacts={formData.contacts || []}
          onChange={(contacts) => setFormData({ ...formData, contacts })}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose} data-testid="supplier-cancel-btn">
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="supplier-save-btn">
          {supplier ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
};

const SupplierCard = ({ supplier, onEdit, onDelete }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const relationLabels = {
    exclusive_importer: 'Importateur exclusif',
    authorized_reseller: 'Revendeur agréé',
    distributor: 'Distributeur'
  };

  const statusColors = {
    active: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
    inactive: 'bg-[#2C3338] text-[#8C8F94] border-[#2C3338]',
    suspended: 'bg-[#2A1515] text-[#E74C3C] border-[#E74C3C]/20'
  };

  return (
    <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] hover:border-[#00E01A]/30 transition-all duration-200 group">
      <div className="flex items-start justify-between p-5 pb-0 mb-4">
        <Link to={`/suppliers/${supplier.id}`} className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 bg-[#141617] rounded-lg flex items-center justify-center flex-shrink-0">
            {supplier.logo_url ? (
              <img src={supplier.logo_url} alt={supplier.name} className="w-10 h-10 object-contain" />
            ) : (
              <Building2 size={24} className="text-[#8C8F94]" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold truncate">{supplier.name}</h3>
            <p className="text-[#8C8F94] text-sm">{supplier.country}</p>
          </div>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 text-[#8C8F94] hover:text-white transition-colors" data-testid={`supplier-menu-${supplier.id}`}>
              <MoreVertical size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#1D2327] border-[#2C3338]">
            <DropdownMenuItem onClick={() => onEdit(supplier)} className="text-white hover:bg-[#2C3338]">
              <Edit size={14} className="mr-2" /> Modifier
            </DropdownMenuItem>
            {supplier.website && (
              <DropdownMenuItem asChild>
                <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-white hover:bg-[#2C3338]">
                  <ExternalLink size={14} className="mr-2" /> Site web
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[#2C3338]" />
            <DropdownMenuItem 
              onClick={() => setDeleteDialogOpen(true)} 
              className="text-[#E74C3C] hover:bg-[#2A1515]"
            >
              <Trash2 size={14} className="mr-2" /> Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Link to={`/suppliers/${supplier.id}`} className="block px-5">
        <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[supplier.status]}`}>
          {supplier.status === 'active' ? 'Actif' : supplier.status === 'inactive' ? 'Inactif' : 'Suspendu'}
        </span>
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#2C3338] text-[#8C8F94] border border-[#2C3338]">
          {relationLabels[supplier.relation_type]}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[#141617] rounded-lg p-3 text-center">
          <p className="text-[#00E01A] font-bold">{supplier.product_count}</p>
          <p className="text-[#8C8F94] text-xs">Produits</p>
        </div>
        <div className="bg-[#141617] rounded-lg p-3 text-center">
          <p className="text-white font-bold">{(supplier.contacts || []).length}</p>
          <p className="text-[#8C8F94] text-xs">Contacts</p>
        </div>
        <div className="bg-[#141617] rounded-lg p-3 text-center">
          <p className={`font-bold ${supplier.active_sav_count > 0 ? 'text-[#F39C12]' : 'text-[#00E01A]'}`}>
            {supplier.active_sav_count}
          </p>
          <p className="text-[#8C8F94] text-xs">SAV</p>
        </div>
      </div>
      </Link>

      <div className="px-5 pb-5">
        <Link
          to={`/suppliers/${supplier.id}`}
          className="flex items-center justify-between text-sm text-[#8C8F94] group-hover:text-[#00E01A] transition-colors pt-3 border-t border-[#2C3338]"
          data-testid={`supplier-link-${supplier.id}`}
        >
          <span>Voir la fiche</span>
          <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1D2327] border-[#2C3338]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer {supplier.name} ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8C8F94]">
              Cette action est irréversible. Le fournisseur sera définitivement supprimé.
              {supplier.product_count > 0 && (
                <span className="block mt-2 text-[#F39C12]">
                  ⚠️ Ce fournisseur a {supplier.product_count} produit(s). Supprimez d'abord les produits.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#2C3338] border-none text-white hover:bg-[#3C4348]">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                onDelete(supplier.id);
                setDeleteDialogOpen(false);
              }}
              className="bg-[#E74C3C] hover:bg-[#C0392B] text-white"
              disabled={supplier.product_count > 0}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Suppliers = () => {
  const { api, isSuperAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, [api]);

  const handleSave = async (data) => {
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, data);
        toast.success('Fournisseur mis à jour');
      } else {
        await api.post('/suppliers', data);
        toast.success('Fournisseur créé');
      }
      fetchSuppliers();
      setDialogOpen(false);
      setEditingSupplier(null);
    } catch (error) {
      console.error('Failed to save supplier:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (supplierId) => {
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.country.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#2C3338] rounded-lg"></div>
                <div>
                  <div className="h-4 w-24 bg-[#2C3338] rounded mb-2"></div>
                  <div className="h-3 w-16 bg-[#2C3338] rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Fournisseurs
          </h1>
          <p className="text-[#8C8F94] mt-1">
            {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''} enregistré{suppliers.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
              data-testid="supplier-search-input"
            />
          </div>
          
          {isSuperAdmin() && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button
                  onClick={() => setEditingSupplier(null)}
                  className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] hover:shadow-[0_0_25px_rgba(0,224,26,0.4)]"
                  data-testid="add-supplier-btn"
                >
                  <Plus size={18} />
                  <span className="hidden md:inline">Ajouter</span>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold font-['Outfit']">
                    {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                  </DialogTitle>
                </DialogHeader>
                <SupplierForm
                  supplier={editingSupplier}
                  onSave={handleSave}
                  onClose={() => {
                    setDialogOpen(false);
                    setEditingSupplier(null);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Suppliers Grid */}
      {filteredSuppliers.length === 0 ? (
        <div className="text-center py-12">
          <Building2 size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">
            {search ? 'Aucun fournisseur trouvé' : 'Aucun fournisseur enregistré'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSuppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={(s) => {
                setEditingSupplier(s);
                setDialogOpen(true);
              }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Suppliers;
